import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router';
import { transformPluginsApi, TransformPluginRow } from './api/transform-plugins-client';
import { IdentityPanel } from './IdentityPanel';
import { FormSchemaEditor } from './FormSchemaEditor';
import { CapabilityPicker } from './CapabilityPicker';
import { ExpressionEditor } from './ExpressionEditor';
import { TestPanel } from './TestPanel';
import '../admin.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface FormFieldDef {
  key: string;
  label: string;
  type: string;
}

function toFormFieldDefs(raw: unknown): FormFieldDef[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (r): r is FormFieldDef =>
      typeof r === 'object' && r !== null && 'key' in r && 'label' in r && 'type' in r,
  );
}

// ---------------------------------------------------------------------------
// ExpressionPanel (inline sub-component)
// ---------------------------------------------------------------------------

interface ExpressionPanelProps {
  plugin: TransformPluginRow;
  onUpdate: (updated: TransformPluginRow) => void;
}

function ExpressionPanel({ plugin, onUpdate }: ExpressionPanelProps) {
  const [expression, setExpression] = useState(plugin.expression);
  const [hostCapabilities, setHostCapabilities] = useState<string[]>(plugin.hostCapabilities);

  const isFirstRender = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      const updated = await transformPluginsApi.update(plugin.id, {
        expression,
        hostCapabilities,
      });
      onUpdate(updated);
    }, 800);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // onUpdate intentionally omitted — mirrors IdentityPanel pattern
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression, hostCapabilities, plugin.id]);

  const formSchemaKeys = toFormFieldDefs(plugin.formSchema).map((f) => f.key);

  return (
    <div className="rb-section">
      <h2 className="rb-section-title">Expression Panel</h2>
      <p className="rb-hint">
        Write a DSL expression that transforms the input value. Built-in functions are always
        available. Host functions (e.g. <code>skarn.lookup</code>) require the corresponding
        product capability to be declared below.
      </p>

      <CapabilityPicker
        capabilities={hostCapabilities}
        onChange={setHostCapabilities}
      />

      <div className="rb-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '4px' }}>
        <label className="rb-label">Expression</label>
        <ExpressionEditor
          value={expression}
          onChange={setExpression}
          capabilities={hostCapabilities}
          tenantFieldNames={[]}
          formSchemaKeys={formSchemaKeys}
        />
        <p className="rb-hint">
          Tenant field names are not yet discoverable via API — pass field names manually
          in <code>field("…")</code> calls. Filed as 52-followup-plugins-tenant-fields-discovery.
        </p>
      </div>
    </div>
  );
}

type TabId = 'identity' | 'form-schema' | 'expression' | 'test';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'identity', label: 'Identity' },
  { id: 'form-schema', label: 'Form schema' },
  { id: 'expression', label: 'Expression' },
  { id: 'test', label: 'Test' },
];

export function TransformPluginDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [plugin, setPlugin] = useState<TransformPluginRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('identity');

  useEffect(() => {
    if (!id) {
      setError('No plugin ID provided');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    transformPluginsApi
      .get(id)
      .then((data) => {
        if (!cancelled) {
          setPlugin(data);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load plugin');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !plugin) {
    return (
      <div className="admin-page">
        <div className="admin-error">Error: {error ?? 'Plugin not found'}</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <Link to="/admin/transform-plugins" className="admin-back">
          &larr; Transform Plugins
        </Link>
        <h1 className="admin-title">{plugin.label}</h1>
        <span className="jt-badge" style={{ marginLeft: 'var(--space-2)' }}>
          {plugin.status}
        </span>
      </div>

      <div className="tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            className={`tab-btn ${activeTab === tab.id ? 'tab-btn--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content" role="tabpanel">
        {activeTab === 'identity' && plugin && (
          <IdentityPanel plugin={plugin} onUpdate={(updated) => setPlugin(updated)} />
        )}
        {activeTab === 'form-schema' && (
          <FormSchemaEditor plugin={plugin} onUpdate={(updated) => setPlugin(updated)} />
        )}
        {activeTab === 'expression' && plugin && (
          <ExpressionPanel plugin={plugin} onUpdate={(updated) => setPlugin(updated)} />
        )}
        {activeTab === 'test' && plugin && <TestPanel plugin={plugin} />}
      </div>
    </div>
  );
}
